import httpApi from './http.api'

const FILE_NAME_PATTERN = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

const fileNameFromHeaders = (headers) => {
  const match = FILE_NAME_PATTERN.exec(headers?.['content-disposition'] || '')
  return match ? decodeURIComponent(match[1]) : null
}

export default class BaseApi {
  constructor(url) {
    this.url = url
  }

  encodeQueryData(data) {
    const ret = []
    for (const d in data) {
      ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]))
    }
    return ret.join('&')
  }

  getUrlWithParams(url, parameters) {
    var querystring = this.encodeQueryData(parameters)
    if (!url.endsWith('/')) {
      url += '/'
    }
    return `${url}?${querystring}`
  }

  getAbsoluteUrl(url = this.url) {
    return `${this.getBaseApi()}${url}`
  }

  getBaseApi() {
    return `${window.config.BASE_API}`
  }

  getBaseMediaUrl() {
    return `${window.config.BASE_MEDIA}`
  }

  getClientInstance() {
    return httpApi
  }

  composeUrl(...parts) {
    const urlSeparator = '/'
    parts = parts.map(p => p ? p.toString() : '')
    const res = parts.filter(p => p.length > 0).map(p => {
      if (p.charAt(0) === urlSeparator) p = p.substr(1)
      if (p.charAt(p.length - 1) === urlSeparator) p = p.substr(0, p.length - 1)
      return p
    }).join(urlSeparator) + urlSeparator

    return res
  }

  async getBlobFileAsync(url, params = {}, data = null) {
    const fullUrl = this.composeUrl(this.url, url)
    const res = await httpApi({ url: fullUrl, method: data ? 'post' : 'get', responseType: 'blob', params, data })
    return { url: window.URL.createObjectURL(new Blob([res.data], { type: res.data.type })), fileName: fileNameFromHeaders(res.headers) }
  }

  async getBlobUrlAsync(url, params = {}, data = null) {
    return (await this.getBlobFileAsync(url, params, data)).url
  }

  async downloadAsync(url, fileName, data = null, config = {}) {
    const downloadUrl = this.composeUrl(this.url, url)
    data = data || {}
    const res = await httpApi({
      url: downloadUrl,
      method: 'get',
      data: data,
      responseType: 'blob',
      ...config
    })
    try {
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName ?? fileNameFromHeaders(res.headers) ?? ''
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error during download:', error)
      throw error
    }
  }

  async getAsync(url, params, config) {
    return httpApi({
      url: this.composeUrl(this.url, url),
      method: 'get',
      params: params,
      ...config
    })
  }

  async getNextIdAsync(resourceUrl = '') {
    return this.getAsync(`${resourceUrl}next-id/`)
  }

  async postAsync(url, data, config) {
    return httpApi({
      url: this.composeUrl(this.url, url),
      method: 'post',
      data: data,
      ...config
    })
  }

  async putAsync(url, data, config) {
    return httpApi({
      url: this.composeUrl(this.url, url),
      method: 'put',
      data: data,
      ...config
    })
  }

  async patchAsync(url, data, config) {
    return httpApi({
      url: this.composeUrl(this.url, url),
      method: 'patch',
      data: data,
      ...config
    })
  }

  async upsertAsync(url, data, config) {
    return data.id ? this.patchAsync(`${url}${data.id}/`, data, config) : this.postAsync(url, data, config)
  }

  async deleteAsync(url, config) {
    return httpApi({
      url: this.composeUrl(this.url, url),
      method: 'delete',
      ...config
    })
  }
}
